// src/redux/thunks/thunk-partners.ts
import { createAsyncThunk } from "@reduxjs/toolkit";
import {
  User,
  User as Partner,
  PartnerTrainerGroup,
  PartnerTrainerEvent,
  Exercise,
  TemplateType,
  MealPlans as MealPlanType,
} from "../../models/types";
import {
  giveClientProAccess,
  removeClientProAccess,
  getIdToken,
} from "../../pages/partners/partners-logged-in/api/avroUsers";

const BASE_URL =
  "https://m01op8z79h.execute-api.us-east-2.amazonaws.com/default/avro-partners";

const ALLUSERS_URL =
  "https://m01op8z79h.execute-api.us-east-2.amazonaws.com/default/avro-users";

/** Helper: widen Partner to include mealPlans for type safety in this module */
type PartnerWithMealPlans = Partner & { mealPlans?: MealPlanType[] };

/** ─────────────────────────────────────────────────────────────────────────────
 * Payload used for fetching the partner record and (optionally) posting a client
 * ────────────────────────────────────────────────────────────────────────────*/
export interface PartnerDataPayload {
  idToken?: string;
  userId: string;
  method?: "GET" | "POST";
  newClientData?: {
    clientUserId: string;
    name: string;
    gender: "male" | "female";
    birthday: Date;
    height: number;
    weight: number;
    activityLevel:
      | "sedentary"
      | "lightly active"
      | "moderately active"
      | "very active"
      | "extra active";
  };
}

/** Fetch/append partner client */
export const fetchPartnerData = createAsyncThunk<
  Partner,
  PartnerDataPayload,
  { rejectValue: string }
>(
  "partners/fetchPartnerData",
  async (
    { idToken, userId, method = "GET", newClientData },
    { rejectWithValue }
  ) => {
    try {
      const token = idToken ?? (await getIdToken());

      let response: Response;

      if (method === "POST") {
        response = await fetch(BASE_URL, {
          method: "POST",
          headers: {
            Authorization: token,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            partnerId: userId,
            newClient: newClientData,
          }),
        });
      } else {
        response = await fetch(
          `${BASE_URL}?userId=${encodeURIComponent(userId)}`,
          {
            method: "GET",
            headers: { Authorization: token },
          }
        );
      }

      if (!response.ok) throw new Error("Network response was not ok");
      const data: Partner = await response.json();
      return data;
    } catch (error: any) {
      return rejectWithValue(error.message ?? "Failed to fetch partner data.");
    }
  }
);

/** ─────────────────────────────────────────────────────────────────────────────
 * All clients with workouts
 * ────────────────────────────────────────────────────────────────────────────*/
export interface WorkoutItem {
  workoutId?: string;
  userId?: string;
  dayOfExecution?: string;
  [key: string]: unknown;
}
export interface PartnerClientMinimal {
  clientUserId: string;
  name?: string;
  gender?: "male" | "female";
  birthday?: string | Date;
  height?: number;
  weight?: number;
  activityLevel?:
    | "sedentary"
    | "lightly active"
    | "moderately active"
    | "very active"
    | "extra active";
}
export interface ClientWithWorkouts extends PartnerClientMinimal {
  userId?: string;
  workouts: WorkoutItem[];
}

export const fetchAllClientsWithWorkouts = createAsyncThunk<
  unknown[],
  { idToken?: string; partnerId: string },
  { rejectValue: string }
>(
  "partners/fetchAllClientsWithWorkouts",
  async ({ idToken, partnerId }, { rejectWithValue }) => {
    try {
      const token = idToken ?? (await getIdToken());

      const url = `${ALLUSERS_URL}?partnerId=${encodeURIComponent(
        partnerId
      )}&include=clientsWithWorkouts`;

      const response = await fetch(url, {
        method: "GET",
        headers: { Authorization: token },
      });

      if (!response.ok)
        throw new Error(`Failed to fetch clients with workouts`);
      const data = (await response.json()) as unknown[];
      return data;
    } catch (error: any) {
      return rejectWithValue(error.message ?? "Unknown error");
    }
  }
);

/** ─────────────────────────────────────────────────────────────────────────────
 * updateTrainerGroups (Optimistic PUT)
 * ────────────────────────────────────────────────────────────────────────────*/
export interface UpdateTrainerGroupsArgs {
  idToken?: string;
  trainerUserId: string;
  groups: PartnerTrainerGroup[];
}

export const updateTrainerGroups = createAsyncThunk<
  Partner,
  UpdateTrainerGroupsArgs,
  { rejectValue: string; state: any }
>(
  "partners/updateTrainerGroups",
  async ({ idToken, trainerUserId, groups }, { getState, rejectWithValue }) => {
    try {
      const token = idToken ?? (await getIdToken());

      const putParams = {
        TableName: "avro-users",
        Key: { userId: trainerUserId },
        UpdateExpression: "SET trainingGroups = :groups",
        ExpressionAttributeValues: {
          ":groups": groups ?? [],
        },
        ReturnValues: "ALL_NEW",
      };

      const putResp = await fetch(BASE_URL, {
        method: "PUT",
        headers: {
          Authorization: token,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(putParams),
      });

      if (!putResp.ok) {
        const msg = await putResp.text().catch(() => "");
        throw new Error(msg || "Failed to PUT trainingGroups");
      }

      const state = getState();
      const current: Partner | null =
        state?.partners?.partnerData?.partnerData ?? null;

      if (!current || current.userId !== trainerUserId) {
        return {
          userId: trainerUserId,
          trainingGroups: groups,
        } as unknown as Partner;
      }
      return { ...current, trainingGroups: groups } as Partner;
    } catch (err: any) {
      return rejectWithValue(err?.message ?? "Failed to update trainer groups");
    }
  }
);

/** ─────────────────────────────────────────────────────────────────────────────
 * updateTrainerEvents (Optimistic PUT to trainerEvents)
 * ────────────────────────────────────────────────────────────────────────────*/
export interface UpdateTrainerEventsArgs {
  idToken?: string;
  trainerUserId: string;
  events: PartnerTrainerEvent[];
}

export const updateTrainerEvents = createAsyncThunk<
  Partner,
  UpdateTrainerEventsArgs,
  { rejectValue: string; state: any }
>(
  "partners/updateTrainerEvents",
  async ({ idToken, trainerUserId, events }, { getState, rejectWithValue }) => {
    try {
      const token = idToken ?? (await getIdToken());

      const putParams = {
        TableName: "avro-users",
        Key: { userId: trainerUserId },
        UpdateExpression: "SET trainerEvents = :events",
        ExpressionAttributeValues: {
          ":events": events ?? [],
        },
        ReturnValues: "ALL_NEW",
      };

      const putResp = await fetch(BASE_URL, {
        method: "PUT",
        headers: {
          Authorization: token,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(putParams),
      });

      if (!putResp.ok) {
        const msg = await putResp.text().catch(() => "");
        throw new Error(msg || "Failed to PUT trainerEvents");
      }

      const state = getState();
      const current: Partner | null =
        state?.partners?.partnerData?.partnerData ?? null;

      if (!current || current.userId !== trainerUserId) {
        return {
          userId: trainerUserId,
          trainerEvents: events,
        } as unknown as Partner;
      }
      return { ...current, trainerEvents: events } as Partner;
    } catch (err: any) {
      return rejectWithValue(err?.message ?? "Failed to update trainer events");
    }
  }
);

/** ─────────────────────────────────────────────────────────────────────────────
 * grant / revoke pro
 * ────────────────────────────────────────────────────────────────────────────*/
export const grantClientPro = createAsyncThunk<
  { client: User; partner: User },
  {
    client: User | string;
    trainerUserId: string;
    clientExtras?: Partial<User>;
    useBearerAuth?: boolean;
    timeoutMs?: number;
  },
  { rejectValue: string }
>("partners/grantClientPro", async (args, { rejectWithValue }) => {
  try {
    const { client, trainerUserId, clientExtras, useBearerAuth, timeoutMs } =
      args;
    const result = await giveClientProAccess(client, trainerUserId, {
      clientExtras,
      useBearerAuth,
      timeoutMs,
    });
    return result;
  } catch (err: any) {
    return rejectWithValue(err?.message || "Failed to grant pro access");
  }
});

export const revokeClientPro = createAsyncThunk<
  { client: User; partner: User },
  {
    client: User | string;
    trainerUserId: string;
    useBearerAuth?: boolean;
    timeoutMs?: number;
  },
  { rejectValue: string }
>("partners/revokeClientPro", async (args, { rejectWithValue }) => {
  try {
    const { client, trainerUserId, useBearerAuth, timeoutMs } = args;
    const result = await removeClientProAccess(client, trainerUserId, {
      useBearerAuth,
      timeoutMs,
    });
    return result;
  } catch (err: any) {
    return rejectWithValue(err?.message || "Failed to revoke pro access");
  }
});

/** ─────────────────────────────────────────────────────────────────────────────
 * updateTrainerCustomExercises
 * ────────────────────────────────────────────────────────────────────────────*/
export interface UpdateTrainerCustomExercisesArgs {
  idToken?: string;
  trainerUserId: string;
  customExercises: Exercise[];
}

export const updateTrainerCustomExercises = createAsyncThunk<
  Partner,
  UpdateTrainerCustomExercisesArgs,
  { rejectValue: string; state: any }
>(
  "partners/updateTrainerCustomExercises",
  async (
    { idToken, trainerUserId, customExercises },
    { getState, rejectWithValue }
  ) => {
    try {
      const token = idToken ?? (await getIdToken());

      const putParams = {
        TableName: "avro-users",
        Key: { userId: trainerUserId },
        UpdateExpression: "SET customExercises = :ex",
        ExpressionAttributeValues: {
          ":ex": customExercises ?? [],
        },
        ReturnValues: "ALL_NEW",
      };

      const putResp = await fetch(BASE_URL, {
        method: "PUT",
        headers: {
          Authorization: token,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(putParams),
      });

      if (!putResp.ok) {
        const msg = await putResp.text().catch(() => "");
        throw new Error(msg || "Failed to PUT customExercises");
      }

      const state = getState();
      const current: Partner | null =
        state?.partners?.partnerData?.partnerData ?? null;

      if (!current || current.userId !== trainerUserId) {
        return {
          userId: trainerUserId,
          customExercises,
        } as unknown as Partner;
      }
      return { ...current, customExercises } as Partner;
    } catch (err: any) {
      return rejectWithValue(
        err?.message ?? "Failed to update trainer custom exercises"
      );
    }
  }
);

// ─────────────────────────────────────────────────────────────
// upsertTrainerTemplate & deleteTrainerTemplate
// ─────────────────────────────────────────────────────────────
export interface UpsertTrainerTemplateArgs {
  idToken?: string;
  trainerUserId: string;
  template: TemplateType;
}

export const upsertTrainerTemplate = createAsyncThunk<
  Partner,
  UpsertTrainerTemplateArgs,
  { rejectValue: string; state: any }
>(
  "partners/upsertTrainerTemplate",
  async (
    { idToken, trainerUserId, template },
    { getState, rejectWithValue }
  ) => {
    try {
      const token = idToken ?? (await getIdToken());

      const state = getState();
      const current: Partner | null =
        state?.partners?.partnerData?.partnerData ?? null;

      const existing = (current?.templates ?? []) as TemplateType[];

      const idx = existing.findIndex((t) => t.id === template.id);
      const nextTemplates =
        idx >= 0
          ? [...existing.slice(0, idx), template, ...existing.slice(idx + 1)]
          : [template, ...existing];

      const putParams = {
        TableName: "avro-users",
        Key: { userId: trainerUserId },
        UpdateExpression: "SET templates = :t",
        ExpressionAttributeValues: { ":t": nextTemplates },
        ReturnValues: "ALL_NEW",
      };

      const putResp = await fetch(BASE_URL, {
        method: "PUT",
        headers: {
          Authorization: token,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(putParams),
      });

      if (!putResp.ok) {
        const msg = await putResp.text().catch(() => "");
        throw new Error(msg || "Failed to PUT templates");
      }

      if (!current || current.userId !== trainerUserId) {
        return {
          userId: trainerUserId,
          templates: nextTemplates,
        } as unknown as Partner;
      }
      return { ...current, templates: nextTemplates } as Partner;
    } catch (err: any) {
      return rejectWithValue(
        err?.message ?? "Failed to upsert trainer template"
      );
    }
  }
);

export const deleteTrainerTemplate = createAsyncThunk<
  Partner,
  { idToken?: string; trainerUserId: string; templateId: string },
  { rejectValue: string; state: any }
>(
  "partners/deleteTrainerTemplate",
  async (
    { idToken, trainerUserId, templateId },
    { getState, rejectWithValue }
  ) => {
    try {
      const token = idToken ?? (await getIdToken());

      const state = getState();
      const current: Partner | null =
        state?.partners?.partnerData?.partnerData ?? null;

      const existing = (current?.templates ?? []) as TemplateType[];
      const nextTemplates = existing.filter((t) => t.id !== templateId);

      const putParams = {
        TableName: "avro-users",
        Key: { userId: trainerUserId },
        UpdateExpression: "SET templates = :t",
        ExpressionAttributeValues: { ":t": nextTemplates },
        ReturnValues: "ALL_NEW",
      };

      const putResp = await fetch(BASE_URL, {
        method: "PUT",
        headers: {
          Authorization: token,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(putParams),
      });

      if (!putResp.ok) {
        const msg = await putResp.text().catch(() => "");
        throw new Error(msg || "Failed to PUT templates");
      }

      if (!current || current.userId !== trainerUserId) {
        return {
          userId: trainerUserId,
          templates: nextTemplates,
        } as unknown as Partner;
      }
      return { ...current, templates: nextTemplates } as Partner;
    } catch (err: any) {
      return rejectWithValue(
        err?.message ?? "Failed to delete trainer template"
      );
    }
  }
);

/** ─────────────────────────────────────────────────────────────────────────────
 * upsertTrainerMealPlans & deleteTrainerMealPlan
 * ────────────────────────────────────────────────────────────────────────────*/
export interface UpsertTrainerMealPlansArgs {
  idToken?: string;
  trainerUserId: string;
  mealPlan: MealPlanType;
}

export const upsertTrainerMealPlans = createAsyncThunk<
  Partner,
  UpsertTrainerMealPlansArgs,
  { rejectValue: string; state: any }
>(
  "partners/upsertTrainerMealPlans",
  async (
    { idToken, trainerUserId, mealPlan },
    { getState, rejectWithValue }
  ) => {
    try {
      const token = idToken ?? (await getIdToken());

      const state = getState();
      const current: PartnerWithMealPlans | null =
        state?.partners?.partnerData?.partnerData ?? null;

      const existing = (current?.mealPlans ?? []) as MealPlanType[];

      const idx = existing.findIndex((p) => p.id === mealPlan.id);
      const nextMealPlans =
        idx >= 0
          ? [...existing.slice(0, idx), mealPlan, ...existing.slice(idx + 1)]
          : [mealPlan, ...existing];

      const putParams = {
        TableName: "avro-users",
        Key: { userId: trainerUserId },
        UpdateExpression: "SET mealPlans = :mp",
        ExpressionAttributeValues: { ":mp": nextMealPlans },
        ReturnValues: "ALL_NEW",
      };

      const putResp = await fetch(BASE_URL, {
        method: "PUT",
        headers: {
          Authorization: token,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(putParams),
      });

      if (!putResp.ok) {
        const msg = await putResp.text().catch(() => "");
        throw new Error(msg || "Failed to PUT mealPlans");
      }

      if (!current || current.userId !== trainerUserId) {
        return {
          userId: trainerUserId,
          mealPlans: nextMealPlans,
        } as unknown as Partner;
      }
      return { ...current, mealPlans: nextMealPlans } as Partner;
    } catch (err: any) {
      return rejectWithValue(err?.message ?? "Failed to upsert meal plan");
    }
  }
);

export const deleteTrainerMealPlan = createAsyncThunk<
  Partner,
  { idToken?: string; trainerUserId: string; planId: string },
  { rejectValue: string; state: any }
>(
  "partners/deleteTrainerMealPlan",
  async ({ idToken, trainerUserId, planId }, { getState, rejectWithValue }) => {
    try {
      const token = idToken ?? (await getIdToken());

      const state = getState();
      const current: PartnerWithMealPlans | null =
        state?.partners?.partnerData?.partnerData ?? null;

      const existing = (current?.mealPlans ?? []) as MealPlanType[];
      const nextMealPlans = existing.filter((p) => p.id !== planId);

      const putParams = {
        TableName: "avro-users",
        Key: { userId: trainerUserId },
        UpdateExpression: "SET mealPlans = :mp",
        ExpressionAttributeValues: { ":mp": nextMealPlans },
        ReturnValues: "ALL_NEW",
      };

      const putResp = await fetch(BASE_URL, {
        method: "PUT",
        headers: {
          Authorization: token,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(putParams),
      });

      if (!putResp.ok) {
        const msg = await putResp.text().catch(() => "");
        throw new Error(msg || "Failed to PUT mealPlans");
      }

      if (!current || current.userId !== trainerUserId) {
        return {
          userId: trainerUserId,
          mealPlans: nextMealPlans,
        } as unknown as Partner;
      }
      return { ...current, mealPlans: nextMealPlans } as Partner;
    } catch (err: any) {
      return rejectWithValue(err?.message ?? "Failed to delete meal plan");
    }
  }
);

/** ─────────────────────────────────────────────────────────────────────────────
 * updateClientGoals
 * ────────────────────────────────────────────────────────────────────────────*/
export interface UpdateClientGoalsArgs {
  idToken?: string;
  clientUserId: string;
  proteinGoal?: number;
  carbsGoal?: number;
  fatsGoal?: number;
  caloriesGoal?: number;
  waterGoal?: {
    units: "ml" | "oz";
    amount: number;
  };
  supplementGoal?: Array<{
    supplementName: string;
    units: string;
    notes: string;
    quantity: string;
    recurring: {
      mon: boolean;
      tue: boolean;
      wed: boolean;
      thu: boolean;
      fri: boolean;
      sat: boolean;
      sun: boolean;
    };
  }>;
}

export const updateClientGoals = createAsyncThunk<
  User,
  UpdateClientGoalsArgs,
  { rejectValue: string }
>("partners/updateClientGoals", async (args, { rejectWithValue }) => {
  try {
    const {
      idToken,
      clientUserId,
      proteinGoal,
      carbsGoal,
      fatsGoal,
      caloriesGoal,
      waterGoal,
      supplementGoal,
    } = args;

    const token = idToken ?? (await getIdToken());

    const setParts: string[] = [];
    const exprValues: Record<string, any> = {};

    if (typeof proteinGoal === "number") {
      setParts.push("goals.proteinGoal = :p");
      exprValues[":p"] = proteinGoal;
    }
    if (typeof carbsGoal === "number") {
      setParts.push("goals.carbsGoal = :c");
      exprValues[":c"] = carbsGoal;
    }
    if (typeof fatsGoal === "number") {
      setParts.push("goals.fatsGoal = :f");
      exprValues[":f"] = fatsGoal;
    }
    if (typeof caloriesGoal === "number") {
      setParts.push("goals.caloriesGoal = :k");
      exprValues[":k"] = caloriesGoal;
    }
    if (waterGoal) {
      setParts.push("goals.waterGoal = :w");
      exprValues[":w"] = waterGoal;
    }
    if (supplementGoal) {
      setParts.push("goals.supplementGoal = :s");
      exprValues[":s"] = supplementGoal;
    }

    if (setParts.length === 0) {
      return {
        userId: clientUserId,
      } as User;
    }

    const putParams = {
      TableName: "avro-users",
      Key: { userId: clientUserId },
      UpdateExpression: `SET ${setParts.join(", ")}`,
      ExpressionAttributeValues: exprValues,
      ReturnValues: "ALL_NEW",
    };

    const resp = await fetch(BASE_URL, {
      method: "PUT",
      headers: {
        Authorization: token,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(putParams),
    });

    if (!resp.ok) {
      const msg = await resp.text().catch(() => "");
      throw new Error(msg || "Failed to update client goals");
    }
    const updated = (await resp.json()) as User;
    return updated;
  } catch (err: any) {
    return rejectWithValue(err?.message ?? "Failed to update client goals");
  }
});

/** ─────────────────────────────────────────────────────────────────────────────
 * updateClientMealPlans
 * ────────────────────────────────────────────────────────────────────────────*/
export interface UpdateClientMealPlansArgs {
  idToken?: string;
  clientUserId: string;
  mealPlans: MealPlanType[];
}

export const updateClientMealPlans = createAsyncThunk<
  User,
  UpdateClientMealPlansArgs,
  { rejectValue: string }
>("partners/updateClientMealPlans", async (args, { rejectWithValue }) => {
  try {
    const { idToken, clientUserId, mealPlans } = args;
    const token = idToken ?? (await getIdToken());

    const putParams = {
      TableName: "avro-users",
      Key: { userId: clientUserId },
      UpdateExpression: "SET mealPlans = :mp",
      ExpressionAttributeValues: {
        ":mp": mealPlans ?? [],
      },
      ReturnValues: "ALL_NEW",
    };

    const resp = await fetch(BASE_URL, {
      method: "PUT",
      headers: {
        Authorization: token,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(putParams),
    });

    if (!resp.ok) {
      const msg = await resp.text().catch(() => "");
      throw new Error(msg || "Failed to update client meal plans");
    }
    const updated = (await resp.json()) as User;
    return updated;
  } catch (err: any) {
    return rejectWithValue(
      err?.message ?? "Failed to update client meal plans"
    );
  }
});
